import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import {
  IdCard,
  Briefcase,
  Banknote,
  User,
  Phone,
  CheckCircle2,
  ChevronRight,
} from 'lucide-react';
import KpiCard from '../components/Admin/KpiCard';
import DisplayField from '../components/UI/DisplayField';

// The same steps as the client screen staff use: one section at a time.
const STEPS = [
  { key: 'personal', label: 'Personal Information', Icon: IdCard },
  { key: 'occupation', label: 'Occupation Information', Icon: Briefcase },
  { key: 'bank', label: 'Bank Information', Icon: Banknote },
];

// One value: the shared read-only field, as everywhere else.
const InfoRow = ({ label, value }) => <DisplayField label={label}>{value || null}</DisplayField>;

const ClientProfile = () => {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionType, setActionType] = useState('info');
  const [step, setStep] = useState('personal');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
  const res = await axios.get(apiUrl('/api/clients/my-profile'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile(res.data);
        setError('');
      } catch (err) {
        setError('Could not fetch profile.');
      }
    };
    fetchProfile();
  }, []);

  const getMessageClass = () => {
    switch (actionType) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700/50';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700/50';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700/50';
      default:
        return 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700/50';
    }
  };

  if (error) {
    return <div className="p-4 text-rose-500 text-sm">{error}</div>;
  }

  if (!profile) {
    return <div className="p-4 text-sm text-gray-500 dark:text-slate-400">Loading...</div>;
  }

  const { personal_info, occupation_info, bank_info } = profile;

  return (
    <div className="max-w-full min-h-screen bg-gray-50 dark:bg-slate-900">

      {/* Who this is, at a glance */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <KpiCard label="Full name" value={personal_info.full_name || '—'} color="blue" icon={<User size={18} />} />
        <KpiCard label="Phone number" value={personal_info.phone_number || '—'} color="green" icon={<Phone size={18} />} />
        <KpiCard label="ID / Passport No" value={personal_info.id_passport_no || '—'} color="amber" icon={<IdCard size={18} />} />
      </div>

      {actionMessage && (
        <div className={`mb-2 p-3 rounded border flex items-center justify-between ${getMessageClass()}`}>
          <span>{actionMessage}</span>
          <button
            onClick={() => setActionMessage('')}
            className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 dark:hover:bg-slate-700 focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            &times;
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-4">
        {/* Steps */}
        <div className="flex items-center gap-1.5 sm:gap-2 mb-4 overflow-x-auto">
          {STEPS.map((st, i) => {
            const active = st.key === step;
            return (
              <React.Fragment key={st.key}>
                {i > 0 && <ChevronRight size={16} className="flex-shrink-0 text-gray-300 dark:text-slate-600" />}
                <button
                  type="button"
                  onClick={() => setStep(st.key)}
                  aria-current={active ? 'step' : undefined}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/40'
                  }`}
                >
                  {active ? <st.Icon size={15} /> : <CheckCircle2 size={15} />}
                  {st.label}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {step === 'personal' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <InfoRow label="Full Name" value={personal_info.full_name} />
            <InfoRow label="ID/Passport No" value={personal_info.id_passport_no} />
            <InfoRow label="Postal Address" value={personal_info.postal_address} />
            <InfoRow label="ERF No" value={personal_info.erf_no} />
            <InfoRow label="Location/Suburb" value={personal_info.suburb} />
            <InfoRow label="City/Town" value={personal_info.city} />
            <InfoRow label="Country" value={personal_info.country} />
            <InfoRow label="Residential Address" value={personal_info.residential_address} />
            <InfoRow label="Phone Number" value={personal_info.phone_number} />
            <InfoRow label="Marital Status" value={personal_info.marital_status} />
            <InfoRow label="Gender" value={personal_info.gender} />
          </div>
        )}

        {step === 'occupation' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <InfoRow label="Occupation" value={occupation_info.occupation} />
            <InfoRow label="Take-home pay" value={occupation_info.salary} />
            <InfoRow label="Employer Name" value={occupation_info.employer_name} />
            <InfoRow label="Employer Phone" value={occupation_info.employer_phone} />
            <InfoRow label="Employer Address" value={occupation_info.employer_address} />
            <InfoRow label="Payslip Number" value={occupation_info.payslip_number} />
          </div>
        )}

        {step === 'bank' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <InfoRow label="Bank Name" value={bank_info.bank_name} />
            <InfoRow label="Branch Code" value={bank_info.branch_code} />
            <InfoRow label="Account Number" value={bank_info.account_number} />
            <InfoRow label="Account Type" value={bank_info.account_type} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientProfile;